import React, { Component, Fragment } from 'react';
import mapboxgl from 'mapbox-gl';
import * as consts from 'shared/consts.js';
import COLORS from 'shared/colors';
import Legend from 'bundles/Dashboard/components/Legend'
import { objIsEqual, getKeyByValue, transformDataForTable, formatAsProperName } from 'shared/helpers';
import { quantile } from 'd3';
import * as turf from '@turf/turf';
import ObjectsToCsv from 'objects-to-csv';
import { saveAs } from 'file-saver';
import moment from 'moment';        
import defaultMarkerImage from 'assets/imgs/map-marker.png';
import activeMarkerImage from 'assets/imgs/map-marker-expired.png';
import railData from 'assets/data/layers/Transit_Rail_Stations.json';
import busData from 'assets/data/layers/MARTA_BusyStops.json';


import { getDate, getMonth, getHours, getMinutes, getSeconds } from "date-fns";


mapboxgl.accessToken = consts.mapBoxAPIKey;

const linearOffset = 5;
const markerHeight = 30;
const markerRadius = 10;

const popupOffset = {
    'top': [0, linearOffset],
    'top-left': [0, linearOffset],
    'top-right': [0, linearOffset],
    'bottom': [0, -(markerHeight + linearOffset)],
    'bottom-left': [0, -(markerHeight + linearOffset)],
    'bottom-right': [0, -(markerHeight + linearOffset)],
    'left': [markerRadius + linearOffset, -(markerHeight - markerRadius)],
    'right': [-(markerRadius + linearOffset), -(markerHeight - markerRadius)],
}

// const classes = {
//     fullWidth: {
//         width: '100%',
//     }
// }

const cc = {
    neg5: '#67001f',
    neg4: '#b2182b',
    neg3: '#d6604d',
    neg2: '#f4a582',
    neg1: '#fddbc7',
    neutral: '#fff',
    pos1: '#d1e5f0',
    pos2: '#92c5de',
    pos3: '#4393c3',
    pos4: '#2166ac',
    pos5: '#053061',
}

class Map extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeGeography: '',
            legendInfo: [],
            fireClickOnGeoLayers: true,
            popUpShow:null
        }
        this.mapContainer = React.createRef();
    }

    componentDidMount() {
        const { 
          housingData, 
          // pipelineData, 
          passRefUpward,
          housingNPUData,
          foodDesertData,
          transportationData,
          transitData,
          lossAffordableHousingData,
          risingRentData,
          raceData,
          filteredCurrentLayerData,
          switches } = this.props

        // console.log(foodDesertData)

        passRefUpward(this.mapContainer.current)

        this.map = new mapboxgl.Map({
            container: this.mapContainer.current,
            // style: 'mapbox://styles/mapbox/streets-v11',
            style: 'mapbox://styles/mapbox/light-v10',
            center: [-84.396163, 33.775078],
            zoom: 11,
            preserveDrawingBuffer: true,
        });

        const map = this.map;
        
        // console.log(fetchHousingNPUData('xx','xxs'));

        map.addControl(new mapboxgl.NavigationControl(), 'bottom-left');
        map.on('load', (event) => {
            if (event.target.style && map.style) {
              map.loadImage(defaultMarkerImage, function (error, image) {
                  if (error) throw error;
                  // console.log(map.style)
                  map.addImage('marker', image);
              })

              map.loadImage(activeMarkerImage, function (error, image) {
                  if (error) throw error;
                  map.addImage('active-marker', image);
              })
            }

            // Food Deserts
            this.loadInterpolatedPolygonGeography(
                map,
                foodDesertData,
                'food-desert-polygons',
                'LILATracts',
                {
                    lightest: COLORS.RED.LIGHTEST,
                    light: COLORS.RED.LIGHT,
                    medium: COLORS.RED.MEDIUM,
                    dark: COLORS.RED.DARK,
                    darkest: COLORS.RED.DARKEST
                }
            )

            // Transportation
            this.loadInterpolatedPolygonGeography(
                map,
                transportationData,
                'transportation-polygons',
                'Pct_Trav_1',
                {
                    lightest: COLORS.BLUE.LIGHTEST,
                    light: COLORS.BLUE.LIGHT,
                    medium: COLORS.BLUE.MEDIUM,
                    dark: COLORS.BLUE.DARK,
                    darkest: COLORS.BLUE.DARKEST
                }
            )

            // Transit
            this.loadInterpolatedPolygonGeography(
                map,
                transitData,
                'transit-polygons',
                // 'BUFF_DIST', // 'OBJECTID',
                'ToBreak',
                {
                    lightest: COLORS.GREEN.LIGHTEST,
                    light: COLORS.GREEN.LIGHT,
                    medium: COLORS.GREEN.MEDIUM,
                    dark: COLORS.GREEN.DARK,
                    darkest: COLORS.GREEN.DARKEST
                }
            )

            // Loss Affordable Housing
            this.loadInterpolatedPolygonGeography(
                map,
                lossAffordableHousingData,
                'loss-affordable-polygons',
                'aCHNG_UN_7',
                {
                    lightest: COLORS.VIOLET.LIGHTEST,
                    light: COLORS.VIOLET.LIGHT,
                    medium: COLORS.VIOLET.MEDIUM,
                    dark: COLORS.VIOLET.DARK,
                    darkest: COLORS.VIOLET.DARKEST
                }
            )

            // Rising Rent
            this.loadInterpolatedPolygonGeography(
                map,
                risingRentData,
                'rising-rent-polygons',
                'diff2014_1',
                {
                    lightest: COLORS.TEAL.LIGHTEST,
                    light: COLORS.TEAL.LIGHT,
                    medium: COLORS.TEAL.MEDIUM,
                    dark: COLORS.TEAL.DARK,
                    darkest: COLORS.TEAL.DARKEST
                }
            )

            // Race
            this.loadInterpolatedPolygonGeography(
                map,
                raceData,
                'race-polygons',
                'BlackNum',
                {
                    lightest: COLORS.SKYBLUE.LIGHTEST,
                    light: COLORS.SKYBLUE.LIGHT,
                    medium: COLORS.SKYBLUE.MEDIUM,
                    dark: COLORS.SKYBLUE.DARK,
                    darkest: COLORS.SKYBLUE.DARKEST
                }
            )

            // Income
            this.loadInterpolatedPolygonGeography(
                map,
                raceData,
                'income-polygons',
                'MedIncNum',
                {
                    lightest: COLORS.EMERALDGREEN.LIGHTEST,
                    light: COLORS.EMERALDGREEN.LIGHT,
                    medium: COLORS.EMERALDGREEN.MEDIUM,
                    dark: COLORS.EMERALDGREEN.DARK,
                    darkest: COLORS.EMERALDGREEN.DARKEST
                }
            )

            // const railData = require('assets/data/layers/Transit_Rail_Stations.json')
            // const busData = require('assets/data/layers/MARTA_BusyStops.json')
           

            this.loadInterpolatedPointGeography(
                map,
                railData,
                'rail-points'
            )

            this.loadInterpolatedPointGeography(
                map,
                busData,
                'bus-points'
            )


            // Data for display geographies
            // const npuData = require('assets/data/geographies/ATL_NPUs.json')
            // const tractData = require('assets/data/geographies/ATL_Tracts.json')
            // const tadData = require('assets/data/geographies/ATL_Tax_Allocation_District.json')
            // const councilData = require('assets/data/geographies/ATL_City_Council_Districtswgs.json')
            // const nsaData = require('assets/data/geographies/ATL_Neighborhood_Statistical_Areas.json')
            // const schoolData = require('assets/data/geographies/ATL_SchoolZones.json')
            const beltlineData = require('assets/data/geographies/Beltline_Trails.json')
            // // *** Display Geographies *** //
            // this.loadPolygonGeography(map, npuData, 'npu-polygons')
            // this.loadPolygonGeography(map, tractData, 'tract-polygons')
            // this.loadPolygonGeography(map, tadData, 'tad-polygons')
            // this.loadPolygonGeography(map, schoolData, 'school-polygons')
            // this.loadPolygonGeography(map, councilData, 'council-polygons')
            // this.loadPolygonGeography(map, nsaData, 'nsa-polygons')

            this.loadLineGeography(map, beltlineData, 'beltline-line')

            // this.setGeography();
            this.setLayerDisplay(map);
            const whichLayer = getKeyByValue(switches, true);
        
            if(whichLayer && filteredCurrentLayerData){
                
                if(whichLayer !== 'transit'){
                this.setLayerDisplayFilter(map,filteredCurrentLayerData,whichLayer)
                }
            }

            if (housingData) {
                this.loadPointData(map, housingData, 'houses', COLORS.THEME, 'marker')
            }

            // console.log(pipelineData)
            // if (pipelineData) {
            //     this.loadPointData(map, pipelineData, 'pipes', 'rgb(115, 173, 150)', 'active-marker')
            // }

            // console.log(housingNPUData)
            if(housingNPUData){
                this.reloadGeoData(map,housingNPUData)
                this.setGeography()
            }


        })
    }

    componentDidUpdate(prevProps,prevState) {
        const { housingData, pipelineData, displayGeography, applyLayerFilterTohouseData, switches, housingNPUData, filteredCurrentLayerData, resetFilteredData,formattedFilters,housingDataOrigin, filteredHousingDataByTransitLayerFilter} = this.props
        const { popUpShow } = this.state
        const map = this.map;
      
        if(!objIsEqual(prevProps.housingNPUData, housingNPUData)){
            const keepTryUntilMapboxStyleLoaded = () => {
                if (!map.isStyleLoaded()){
                    setTimeout(keepTryUntilMapboxStyleLoaded, 500);
                }else{
                    this.reloadGeoData(map,housingNPUData)
                    this.setGeography();
                }

            }

            keepTryUntilMapboxStyleLoaded()
  
        }
        

        if (!objIsEqual(prevProps.housingData, housingData)) {

            if (popUpShow && popUpShow.isOpen()) popUpShow.remove();
            
            this.reloadPointData(map, housingData)
        }

        if (!objIsEqual(prevProps.pipelineData, pipelineData)) {

            if (popUpShow && popUpShow.isOpen()) popUpShow.remove();

            this.reloadPointDataPipeline(map, pipelineData)
        }
         

        if (prevProps.displayGeography !== displayGeography) {
            this.setGeography();
        }

        if (!objIsEqual(prevProps.switches, switches)) {
            this.setLayerDisplay(map)
            const whichLayer = getKeyByValue(switches, true);

            // const whichPreLayer = getKeyByValue(prevProps.switches, true);
            //console.log(whichLayer)
        
                if(whichLayer && filteredCurrentLayerData){
                    
                    if(whichLayer !== 'transit'){
                    this.setLayerDisplayFilter(map,filteredCurrentLayerData,whichLayer)

                    const filteredHousingDataByLayerFilter = housingDataOrigin.features.filter(function(item){ 
                        for(var poly of filteredCurrentLayerData.features) {
                            var isIn = turf.inside(item, poly);
                            if(isIn){
                                return true
                            }
                        }
                        return false
                    })

                    applyLayerFilterTohouseData(filteredHousingDataByLayerFilter)


                    }
                }

                if(whichLayer === 'transit'&& filteredHousingDataByTransitLayerFilter){
                    applyLayerFilterTohouseData(filteredHousingDataByTransitLayerFilter)
                }

                if(!whichLayer){
                    resetFilteredData(formattedFilters,housingDataOrigin)
                }
            


            // else{
            //     resetRange();
            // }

        }

        if (!objIsEqual(prevProps.filteredCurrentLayerData, filteredCurrentLayerData)) {
           
            const whichLayer = getKeyByValue(switches, true);
            // console.log(whichLayer)
            // console.log(filteredCurrentLayerData)
            if(whichLayer){
                if(whichLayer !== 'transit' ){
                    this.setLayerDisplayFilter(map,filteredCurrentLayerData,whichLayer)
                }
            }

        }
    }

    componentWillUnmount() {
        this.map.remove();
    }

    setGeography = () => {
        const { displayGeography } = this.props;

        const mapping = {
            '': () => {
                this.setVisibility('')
            },
            'Beltline': () => {
                this.setVisibility('beltline-line')
            },
            'Census Tract': () => {
                this.setVisibility('tract-polygons')
            },
            'Council District': () => {
                this.setVisibility('council-polygons')
            },
            'Neighborhood Statistical Area': () => {
                this.setVisibility('nsa-polygons')
            },
            'NPU': () => {
                this.setVisibility('npu-polygons')
            },
            'School District': () => {
                this.setVisibility('school-polygons')
            },
            'High School District': () => {
                this.setVisibility('highSchool-polygons')
            },
            'TAD': () => {
                this.setVisibility('tad-polygons')
            },
        }
        mapping[displayGeography]()

        //bring the housing point layer to the top
        if (this.map.getSource("houses")){
            this.map.moveLayer( 'unclustered-point-houses');
            this.map.moveLayer( 'clusters-houses');
            this.map.moveLayer( 'cluster-count-houses');
        }

        if (this.map.getSource("pipes")){
            this.map.moveLayer( 'unclustered-point-pipes');
            this.map.moveLayer( 'clusters-pipes');
            this.map.moveLayer( 'cluster-count-pipes');
        }
    }

    setLayerDisplay = (map) => {
        const { switches } = this.props;

        const layerMapping = {
            foodDeserts: 'food-desert-polygons',
            transportation: 'transportation-polygons',
            transit: 'transit-polygons',
            affordableUnits: 'loss-affordable-polygons',
            risingRents: 'rising-rent-polygons',
            race: 'race-polygons',
            income: 'income-polygons',
        }

        const keys = Object.keys(switches)
        keys.forEach(key => {
            if (key !== 'transit'){
                map.setLayoutProperty(layerMapping[key], 'visibility', switches[key] ? 'visible' : 'none')
            }else{
                map.setLayoutProperty(layerMapping[key], 'visibility', switches[key] ? 'visible' : 'none')
                map.setLayoutProperty('rail-points', 'visibility', switches[key] ? 'visible' : 'none')
                map.setLayoutProperty('bus-points', 'visibility', switches[key] ? 'visible' : 'none')
            }
        })

    }

    setLayerDisplayFilter = (map,transportationDataFilter,whichLayer) => {

        const layerMapping = {
            "foodDeserts": 'food-desert-polygons',
            "transportation": 'transportation-polygons',
            "transit": 'transit-polygons',
            "affordableUnits": 'loss-affordable-polygons',
            "risingRents": 'rising-rent-polygons',
            "race": 'race-polygons',
            "income": 'income-polygons',
        }

        const layerIndex = {
            "foodDeserts": 'OBJECTID_1',
            "transportation": 'OBJECTID_1',
            "transit": 'ORIG_FID',
            "affordableUnits": 'OBJECTID',
            "risingRents": 'OBJECTID_1',
            "race": 'GEOID',
            "income": 'GEOID',
        }
        
        // const keys = Object.keys(layersData)
        // console.log(transportationDataFilter)

        if (transportationDataFilter.features.length) {
            // keys.forEach(key => {
                map.setFilter(layerMapping[whichLayer], [
                    'match',
                    ['get', layerIndex[whichLayer]],
                    transportationDataFilter.features.map(function(feature) {
                        return feature.properties[layerIndex[whichLayer]];
                    }),
                    true,
                    false
                    ]);
            
            // })
        }
    }

    setVisibility = (id) => {
        const { activeGeography } = this.state;
        const map = this.map;
        // console.log('Map:', activeGeography)
        if (activeGeography) { // null out the state first and use callback for setState to set visibility
            map.setLayoutProperty(activeGeography, 'visibility', 'none')
            map.setLayoutProperty(activeGeography + '-label', 'visibility', 'none')
            if(activeGeography !== "beltline-line"){
                map.setLayoutProperty(activeGeography + '-line', 'visibility', 'none')
            }
        }
        if (id) {
            map.setLayoutProperty(id, 'visibility', 'visible')
            map.setLayoutProperty(id + '-label', 'visibility', 'visible')
            if(id !== "beltline-line"){
                map.setLayoutProperty(id + '-line', 'visibility', 'visible')
            }
        }
        this.setState({ activeGeography: id })
    }

    reloadGeoData = (map,updatedData)=> {
        if(map.getLayer('npu-polygons')){

            map.off('click', 'npu-polygons', this.onClick);
            map.off('click', 'tract-polygons', this.onClick);
            map.off('click', 'tad-polygons', this.onClick);
            map.off('click', 'school-polygons', this.onClick);
            map.off('click', 'highSchool-polygons', this.onClick);
            map.off('click', 'council-polygons', this.onClick);
            map.off('click', 'nsa-polygons', this.onClick);

            map.removeLayer('npu-polygons')
            map.removeLayer('tract-polygons')
            map.removeLayer('tad-polygons')
            map.removeLayer('school-polygons')
            map.removeLayer('highSchool-polygons')
            map.removeLayer('council-polygons')
            map.removeLayer('nsa-polygons')
            map.removeLayer('npu-polygons-line')
            map.removeLayer('tract-polygons-line')
            map.removeLayer('tad-polygons-line')
            map.removeLayer('school-polygons-line')
            map.removeLayer('highSchool-polygons-line')
            map.removeLayer('council-polygons-line')
            map.removeLayer('nsa-polygons-line')
            map.removeLayer('npu-polygons-label')
            map.removeLayer('tract-polygons-label')
            map.removeLayer('tad-polygons-label')
            map.removeLayer('school-polygons-label')
            map.removeLayer('highSchool-polygons-label')
            map.removeLayer('council-polygons-label')
            map.removeLayer('nsa-polygons-label')

            map.removeSource('npu-polygons-dataSource')
            map.removeSource('tract-polygons-dataSource')
            map.removeSource('tad-polygons-dataSource')
            map.removeSource('school-polygons-dataSource')
            map.removeSource('highSchool-polygons-dataSource')
            map.removeSource('council-polygons-dataSource')
            map.removeSource('nsa-polygons-dataSource')
            // map.removeSource('npu-polygons-line')
            // map.removeSource('tract-polygons-line')
            // map.removeSource('tad-polygons-line')
            // map.removeSource('school-polygons-line')
            // map.removeSource('highSchool-polygons-line')
            // map.removeSource('council-polygons-line')
            // map.removeSource('nsa-polygons-line')
        }

        // console.log(map)


        this.updateGeoData(map, updatedData)
    }

    reloadPointData = (map, updatedData) => {
        map.removeLayer('clusters-houses')
        map.removeLayer('cluster-count-houses')
        map.removeLayer('unclustered-point-houses')
        map.removeSource('houses')

        this.updatePointData(map, updatedData, 'houses', COLORS.THEME, 'marker')
    
    }

    reloadPointDataPipeline = (map, updatedData) => {

        map.removeLayer('clusters-pipes')
        map.removeLayer('cluster-count-pipes')
        map.removeLayer('unclustered-point-pipes')
        map.removeSource('pipes')

        this.updatePointData(map, updatedData, 'pipes', 'rgb(115, 173, 150)', 'active-marker')
    }

    onMouseenterForClusters = (e) =>{
        this.map.getCanvas().style.cursor = 'pointer';
        this.setState({ fireClickOnGeoLayers: false })
    }

    onMouseleaveForClusters = (e) =>{
        this.map.getCanvas().style.cursor = '';
        this.setState({ fireClickOnGeoLayers: true })
    }

    onMouseenterForUnclustered = (e) =>{
        this.map.getCanvas().style.cursor = 'pointer';
        this.setState({ fireClickOnGeoLayers: false })
    }

    onMouseleaveForUnclustered = (e) =>{
        this.map.getCanvas().style.cursor = '';
        this.setState({ fireClickOnGeoLayers: true })
    }

    loadPointData = (map, housingData, dataType, color, marker) => {
        // map.on('styleimagemissing', function () {
        //     console.log('hiiiya', map.hasImage('marker'))
        //     map.addImage('marker');
        // })
        this.updatePointData(map, housingData, dataType, color, marker)
        // console.log(housingData)

        map.on('click', 'clusters-'+dataType, function (e) {
            var features = map.queryRenderedFeatures(e.point, { layers: ['clusters-'+dataType] });
            var clusterId = features[0].properties.cluster_id;
            map.getSource(dataType).getClusterExpansionZoom(clusterId, function (err, zoom) {
                if (err)
                    return;

                map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom
                });
            });
        });
        
        dataType === 'houses'?
        map.on('click', 'unclustered-point-'+dataType, this.showPopup)
        :map.on('click', 'unclustered-point-'+dataType, this.showPopupPipeline)
        

        map.on('mouseenter', 'clusters-'+dataType, this.onMouseenterForClusters);

        map.on('mouseleave', 'clusters-'+dataType, this.onMouseleaveForClusters);

        map.on('mouseenter', 'unclustered-point-'+dataType, this.onMouseenterForUnclustered);

        map.on('mouseleave', 'unclustered-point-'+dataType, this.onMouseleaveForUnclustered);

        // map.on('styleimagemissing', function (event) {
        //     console.log('hiiiya', event)
        //     map.loadImage(require('assets/imgs/map-marker.png'), function (error, image) {
        //         map.addImage(event.id, image);
        //     })
        // })
    }

    // loadGeoData = (map, housingData) => {
    //     this.updateGeoData(map, housingData)
    // }

    async downloadDataForSelectedGeo (displayGeography,regionName,housingData,filteredGeoUnit) {
        // find those points within polygon
        const filteredHousingDataByGeoUnit = await housingData.features.filter(item => turf.inside(item, filteredGeoUnit[0]))
        // console.log(filteredHousingDataByGeoUnit);
        // --------- CSV ---------
        const flatData =  transformDataForTable(filteredHousingDataByGeoUnit)
        let csv = new ObjectsToCsv(flatData);
        let res = await csv.toString();
        const csvBlob = new Blob([res], { type: 'text/csv' });
        const date = new Date();
        saveAs(csvBlob, `${displayGeography}_${regionName}_HouseATLData_${getMonth(date)}_${getDate(date)}_${getHours(date)}-${getMinutes(date)}-${getSeconds(date)}.csv`);
    }

    onClick = (e) => {
        const { fireClickOnGeoLayers } = this.state;
        if (fireClickOnGeoLayers){
        const { displayGeography, housingData, housingNPUData } = this.props;
        
        

        const { Total_Unit, Sub_Unit, Risk_Unit } = e.features[0].properties;

        // console.log(e.features[0]);

        var regionName;
        var layerName;
        var geoUnitFeildName;

        const mapping = {
            '': () => {
                 regionName = 'n/a'
                 layerName = 'n/a'
                 geoUnitFeildName = 'n/a'
            },
            'Beltline': () => {
                 regionName = 'n/a'
                 layerName = 'n/a'
                 geoUnitFeildName = 'n/a'
            },
            'Census Tract': () => {
                 regionName = e.features[0].properties["NAMELSAD10"]
                 layerName = 'tract'
                 geoUnitFeildName = 'NAMELSAD10'
            },
            'Council District': () => {
                 regionName = e.features[0].properties["CCName"]
                 layerName = 'council'
                 geoUnitFeildName = 'CCName'
            },
            'Neighborhood Statistical Area': () => {
                 regionName = e.features[0].properties["NEIGHBORHO"]
                 layerName = 'neighborhood'
                 geoUnitFeildName = 'NEIGHBORHO'
            },
            'NPU': () => {
                 regionName = e.features[0].properties["NAME"]
                 layerName = 'npu'
                 geoUnitFeildName = 'NAME'
            },
            'School District': () => {
                 regionName = e.features[0].properties["PropES"]
                 layerName = 'school'
                 geoUnitFeildName = 'PropES'
            },
            'High School District': () => {
                regionName = e.features[0].properties["PropHS"]
                layerName = 'highSchool'
                geoUnitFeildName = 'PropHS'
           },
            'TAD': () => {
                 regionName = e.features[0].properties["ZONEDESC"]
                 layerName = 'tax'
                 geoUnitFeildName = 'ZONEDESC'
            },
        }

        mapping[displayGeography]()


        // console.log(housingData);
        // console.log(housingNPUData[layerName]);
        const filteredGeoUnit = housingNPUData[layerName].features.filter(item => item.properties[geoUnitFeildName] === regionName)
        // console.log(filteredGeoUnit);
        
        // console.log(id)
        // console.log(map.queryRenderedFeatures(e.point, { layers: [id] }))
        new mapboxgl.Popup({ className: 'p-window' })
            .setLngLat(e.lngLat)
            .setHTML(
                `
                 <div class="p-container">
                    <h6 class="p-title">${regionName}</h6>
                    <div class="p-divider"></div>
                    <div class="p-row">
                        <p class="p-label">Total Properties:</p>
                        <p class="p-value">${Total_Unit}</p>
                    </div>
                    <div class="p-row">
                        <p class="p-label">Total Subsidized Units:</p>
                        <p class="p-value">${(Sub_Unit).toLocaleString()}</p>
                    </div>
                    <div class="p-row">
                        <p class="p-label">Units at-risk of expiring:</p>
                        <p class="p-value">${Risk_Unit}</p>
                    </div>
                    <div className="bottom-bar">
                        <button
                        style="width: 100%;
                        margin-top: 10px;
                        color:#fff;
                        background-color:#d82238;
                        padding: 6px 16px;
                        font-size: 0.875rem;
                        min-width: 64px;
                        box-sizing: border-box;
                        transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
                        font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
                        font-weight: 500;
                        line-height: 1.75;
                        border-radius: 4px;
                        letter-spacing: 0.02857em;
                        border: 0;
                        padding: 8px 24px;
                        box-shadow: none;
                        text-transform: uppercase;"

                        onMouseOver="this.style.backgroundColor='rgb(151, 23, 39)'"
                        onMouseOut="this.style.backgroundColor='#d82238'"

                        id="downloadButtonInTooltipFor${regionName}"
                        >
                            Download Housing Data
                        </button>
                    </div>
                </div>
                `)
            .addTo(this.map);
        
        document.getElementById('downloadButtonInTooltipFor'+regionName).addEventListener('click', this.downloadDataForSelectedGeo.bind(null,displayGeography,regionName,housingData,filteredGeoUnit),false);
        }
    }

    loadDisplayGeographyInteractions = (map, id, key) => {
        // const { displayGeography} = this.props;
        // var geoUnitFeildName;
        // const mapping = {
        //     '': () => {
  
        //          geoUnitFeildName = 'n/a'
        //     },
        //     'Beltline': () => {

        //          geoUnitFeildName = 'n/a'
        //     },
        //     'Census Tract': () => {
    
        //          geoUnitFeildName = 'NAMELSAD10'
        //     },
        //     'Council District': () => {
    
        //          geoUnitFeildName = 'CCName'
        //     },
        //     'Neighborhood Statistical Area': () => {

        //          geoUnitFeildName = 'NEIGHBORHO'
        //     },
        //     'NPU': () => {
   
        //          geoUnitFeildName = 'NAME'
        //     },
        //     'School District': () => {

        //          geoUnitFeildName = 'PropES'
        //     },
        //     'High School District': () => {

        //         geoUnitFeildName = 'PropHS'
        //    },
        //     'TAD': () => {

        //          geoUnitFeildName = 'ZONEDESC'
        //     },
        // }

        // mapping[displayGeography]()

        map.on('click', id, this.onClick);
        var hoveredUnitId = null;
        // Change the cursor to a pointer when the mouse is over the states layer.
        map.on('mousemove', id, function (e) {
            map.getCanvas().style.cursor = 'pointer';
            
            if (e.features.length > 0) {
                if (hoveredUnitId) {
                    map.setFeatureState(
                        { source: id + '-dataSource', id: hoveredUnitId },
                        { hover: false }
                    );
                }
             
                hoveredUnitId = e.features[0].id;
                map.setFeatureState(
                    { source: id + '-dataSource', id: hoveredUnitId },
                    { hover: true }
                );
            }
        });

        // Change it back to a pointer when it leaves.
        map.on('mouseleave', id, function () {
            map.getCanvas().style.cursor = '';
            if (hoveredUnitId) {
                map.setFeatureState(
                    { source: id + '-dataSource', id: hoveredUnitId },
                    { hover: false }
                );
            }
            hoveredUnitId = null;
        });
    }

    updateGeoData = (map, geoData) => {
        const npuData = geoData["npu"]
        const tractData = geoData["tract"]
        const tadData = geoData["tax"]
        const councilData = geoData["council"]
        const nsaData = geoData["neighborhood"]
        const schoolData = geoData["school"]
        const highSchoolData = geoData["highSchool"]

        // console.log(npuData)

        this.loadPolygonGeography(map, npuData, 'npu-polygons')
        this.loadPolygonGeography(map, tractData, 'tract-polygons')
        this.loadPolygonGeography(map, tadData, 'tad-polygons')
        // console.log(schoolData)
        this.loadPolygonGeography(map, schoolData, 'school-polygons')
        this.loadPolygonGeography(map, highSchoolData, 'highSchool-polygons')

        this.loadPolygonGeography(map, councilData, 'council-polygons')
        this.loadPolygonGeography(map, nsaData, 'nsa-polygons')

        
    }

    updatePointData = (map, housingData, dataType, color, marker) => {
        map.addSource(dataType, {
            type: "geojson",
            data: housingData,
            cluster: true,
            clusterMaxZoom: 14,
        });

        map.addLayer({
            id: "clusters-"+dataType,
            type: "circle",
            source: dataType,
            // filter: ["all", ["==", "Aggregated", 1], ["has", "point_count"]],
            filter: ["has", "point_count"],
            paint: {
                // "circle-color": [
                //     "step",
                //     ["get", "point_count"],
                //     "#4285f4",
                //     100,
                //     "#fac940",
                //     750,
                //     "#ff914a"
                // ],
                "circle-color": color,
                "circle-stroke-width": 1,
                "circle-stroke-color": "#fff",
                "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    16, // radius
                    10, // <--- value
                    20, // radius
                    25, // <--- value
                    23,
                    50, // <--- value
                    26,
                    100, // <--- value
                    34,
                    250,
                    40
                ]
            }
        });

        map.addLayer({
            id: "cluster-count-"+dataType,
            type: "symbol",
            source: dataType,
            // filter: ["all", ["==", "Aggregated", 1], ["has", "point_count"]],
            filter: ["has", "point_count"],
            paint: {
                "text-color": '#fff',
            },
            layout: {
                "text-field": "{point_count_abbreviated}",
                "text-size": 14,
                "text-font": ["Arial Unicode MS Bold"],
            }
        });

        map.addLayer({
            id: "unclustered-point-"+dataType,
            type: "symbol",
            source: dataType,
            // filter: ["all", ["==", "Aggregated", 1], ["!has", "point_count"]],
            filter: ["!has", "point_count"],
            // paint: {
            //     "circle-color": "#dc1e33",
            //     "circle-radius": 4,
            //     "circle-stroke-width": 1,
            //     "circle-stroke-color": "#fff"
            // }
            layout: {
                // "icon-image": ["case",
                //     ["get", "active"], 'marker',
                //     'active-marker'],
                "icon-image": marker,
                "icon-size": 0.06,
                'icon-anchor': 'bottom',
            }
        });
    }

    showPopupPipeline = (e) => {
        const { geometry, properties } = e.features[0];
        // console.log(properties)
        const coordinates = geometry.coordinates.slice();

        
        const {
            ProjectName,
            NewConstructionUnits,
            PreservedUnits,
            RentalUnits,
            ForSaleUnits,
            Affordability_Below30AMI,
            Affordability_Below50AMI,
            Affordability_Below60AMI,
            Affordability_Below80AMI,
            Affordability_Below100AMI,
            Affordability_Below120AMI,
            Affordability_Above120AMI
        } = properties;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        var popup = new mapboxgl.Popup({ offset: popupOffset, className: 'p-window' })
            .setLngLat(coordinates)
            .setHTML(`
                    <div class="p-container">
                        <h6 class="p-title">${ProjectName}</h6>
                        <div class="p-divider"></div>
                        <div class="p-row">
                            <p class="p-label">New Construction Units:</p>
                            
                            <p class="p-value">${NewConstructionUnits} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Preserved Units:</p>
                            <p class="p-value">${PreservedUnits} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Rental Units:</p>
                            <p class="p-value">${RentalUnits} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">For Sale Units:</p>
                            <p class="p-value">${ForSaleUnits} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Below 30 AMI:</p>
                            <p class="p-value">${Affordability_Below30AMI} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Below 50 AMI:</p>
                            <p class="p-value">${Affordability_Below50AMI} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Below 60 AMI:</p>
                            <p class="p-value">${Affordability_Below60AMI} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Below 80 AMI:</p>
                            <p class="p-value">${Affordability_Below80AMI} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Below 100 AMI:</p>
                            <p class="p-value">${Affordability_Below100AMI} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Below 120 AMI:</p>
                            <p class="p-value">${Affordability_Below120AMI} units</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Affordability Above 120 AMI:</p>
                            <p class="p-value">${Affordability_Above120AMI} units</p>
                        </div>
                    </div>
                `)

            this.setState({popUpShow: popup})

            popup.addTo(this.map);

            // console.log(this.map)

    }

    showPopup = (e) => {
        // const { displayGeography } = this.props;
        // console.log(displayGeography)
        // if(displayGeography === '' || displayGeography === 'Beltline'){
        // console.log("point click ")
        const { geometry, properties } = e.features[0];
        // console.log(e.features)
        const coordinates = geometry.coordinates.slice();

        
        const {
            address,
            totalUnits,
            // totalSubsidizedUnits,
            // totalPercentSubsidized,
            subsidies,
            ownerName
        } = properties;

        const addressArr = address.split(',');
        // console.log(JSON.parse(subsidies))

        //find the latest date across the subsidies 
        var latestDate = new Date(Math.max.apply(null, JSON.parse(subsidies)
          .filter(item => item.SubStart)
          .map(item => new Date(item.SubStart)
          )));

        const subsidyArray = JSON.parse(subsidies).filter(item => item.project_name && item.project_name !== '')

        //find the latest subsidy
        var latestSubsidy = subsidyArray.filter(function(item){
            var subsidyStartDate = new Date(item.SubStart);
            return subsidyStartDate.getTime() === latestDate.getTime();
        })[0];

        

        const name = latestSubsidy 
          ? latestSubsidy.project_name 
          : subsidyArray.filter(item => item.project_name && item.project_name !== '')[0] 
            ? subsidyArray.filter(item => item.project_name && item.project_name !== '')[0].project_name
            : 'Name Unknown';

        //console.log(latestDate)

        // const formatAsProperName = text =>
        //  text.toLowerCase()
        //  .split(' ')
        //  .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        //  .join(' ')

        let otherNames = '';
        let namesTemp = '';
        let nameArray = [];

        if (JSON.parse(subsidies).length > 1){
    
            JSON.parse(subsidies).forEach((obj, i) => {
                
                if (nameArray.includes(formatAsProperName(obj.project_name)) ||
                  formatAsProperName(name) === formatAsProperName(obj.project_name)
                ){
                    return;
                }else{
                    nameArray.push(formatAsProperName(obj.project_name));
                    namesTemp += `
                    ${formatAsProperName(obj.project_name)}  & `
                }
                
            })

            namesTemp = namesTemp.slice(0, -2); 
            
            otherNames = 
            `<div class="p-subtitle">
              ${formatAsProperName(namesTemp)}
            </div>`
        }else{
            otherNames = ''
        }

        let template = '';
        JSON.parse(subsidies).forEach((obj, i) => {
            const {
                Funding,
                RiskUnits,
                Stewarding,
                SubStart,
                SubEnd,
                UnitsSub,
                tenantType,
                project_name,
                housingType,
                updated_on,
                riskOfExp
            } = obj;
            const updateDate = new Date(updated_on)

            // let month = updateDate.getMonth()+1;
            // let dt = updateDate.getDate();

            // if (dt < 10) {
            //     dt = '0' + dt;
            //   }
            //   if (month < 10) {
            //     month = '0' + month;
            //   }

            // let updateDate  = month +'/' +  dt + '/'+updateDate.getFullYear();
            // <p class="p-value">${Funding[0]}</p>
            // <div class="p-row">
            //     <p class="p-subtitle">Project Name: ${project_name}</p>
            // </div>
            // <div class="p-row">
            template += `
                <div class="p-divider"></div>
                <div class="p-row">
                    <p class="p-label">Project Name:</p>
                    <p class="p-value">${project_name}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">Funding Source:</p>
                    
                    <p class="p-value">${Funding.toString()}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">Subsidy Start:</p>
                    <p class="p-value">${SubStart}</p>
                </div>
                ${
                  riskOfExp
                    ?  `<div class="p-row">
                          <p class="p-label">Risk of Early Expiration (LIHTC only):</p>
                          <p class="p-value">${riskOfExp}</p>
                      </div>`
                    : ''
                }
                <div class="p-row">
                    <p class="p-label">Subsidy End:</p>
                    <p class="p-value">${SubEnd}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">Subsidy Units:</p>
                    <p class="p-value">${UnitsSub}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">Stewarding Agency:</p>
                    <p class="p-value">${Stewarding}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">Target Population:</p>
                    <p class="p-value">${tenantType}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">Development Type:</p>
                    <p class="p-value">${housingType}</p>
                </div>
                <div class="p-row">
                    <p class="p-label">At-Risk:</p>
                    <p class="p-value">${RiskUnits}</p>
                </div>
                <div class="p-row">
                    <p class="p-value" style="font-size:11px;margin:5px 0 10px 0">Last updated on ${moment(updateDate).format('M/D/YYYY')}</p>
                </div>
            `
        })

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        // TODO: delete?
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        var popup = new mapboxgl.Popup({ offset: popupOffset, className: 'p-window' })
            .setLngLat(coordinates)
            .setHTML(`
                    <div class="p-container">
                        <h6 class="p-title">${name}</h6>
                        <div class="p-subtitle">
                        ${otherNames}
                        </div>
                        <div class="p-divider"></div>
                        <div class="p-row">
                            <p class="p-label">Owner Name:</p>
                            <p class="p-value">${ownerName}</p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Address:</p>
                            <p class="p-value">
                                ${addressArr[0]}
                                <span style="display: block;">
                                    ${[addressArr[1].trim(), addressArr[2]].join(',')}
                                </span>
                            </p>
                        </div>
                        <div class="p-row">
                            <p class="p-label">Total Units:</p>
                            <p class="p-value">${totalUnits}</p>
                        </div>

                        <h5 class='subsidy-section-header'>Subsidy Info</h5>

                        ${template}
                    </div>
                `)
            //     <div class="p-row">
            //     <p class="p-label">Total Subsidized Units:</p>
            //     <p class="p-value">${totalSubsidizedUnits}</p>
            // </div>
            // <div class="p-row">
            //     <p class="p-label">Total Percent Subsidized:</p>
            //     <p class="p-value">${(totalPercentSubsidized / 100).toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 1 })}</p>
            // </div>

            this.setState({popUpShow: popup})

            popup.addTo(this.map);
        
        // }
    }

    loadPolygonGeography = (map, data, id) => {
        
        // console.log(data);
        const idWithLine = id + '-line'
        const idDataSource = id + '-dataSource'
        const idWithLabel = id + '-label'

        map.addSource(idDataSource, {
            type: 'geojson',
            data: data,
            generateId: true
        });

        map.addLayer({
            id: id,
            type: 'fill',
            source: idDataSource,
            layout: {},
            paint: {
                'fill-color': '#85C1E9',
                'fill-outline-color': '#3498DB',
                'fill-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    0.6,
                    0.3
                    ]
            },
        })

        map.addLayer({
            id: idWithLine,
            type: 'line',
            source: idDataSource,
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-color': '#3498DB',
                'line-width': 1,
                
            },
        })

        const mappingToLayerName = {
            'npu-polygons': 'NAME', //NPUName
            'tract-polygons': 'NAMELSAD10',
            'tad-polygons': 'ZONEDESC',
            'school-polygons': 'PropES', //SZName
            'highSchool-polygons': 'PropHS',
            'council-polygons': 'CCName',
            'nsa-polygons': 'NEIGHBORHO',
        }

        // add lable layer based on zoom level 
        map.addLayer({
            id: idWithLabel,
            type: "symbol",
            source: idDataSource,
            minzoom: 4, // Set zoom level to whatever suits your needs
            layout: {
                "text-field": ['get', mappingToLayerName[id]],
                "text-anchor": "center",
                "text-offset": [0,0.5],
                'text-font': [
                    'Open Sans Bold',
                    'Arial Unicode MS Bold'
                    ],
                'text-size': 11,
                'text-transform': 'uppercase',
                'text-letter-spacing': 0.05,
            },
            paint: {
                'text-color': '#202',
                'text-halo-color': '#fff',
                'text-halo-width': 2
            },
        });

        
        map.setLayoutProperty(id, 'visibility', 'none')
        map.setLayoutProperty(idWithLine, 'visibility', 'none')
        map.setLayoutProperty(idWithLabel, 'visibility', 'none')

        // console.log(id)
        this.loadDisplayGeographyInteractions(map, id, mappingToLayerName[id])
    }

    getPaintProps = (array, id, variable, { lightest, light, medium, dark, darkest }) => {
        const otherStyles = {
            'fill-outline-color': 'rgb(20, 20, 20)',
            'fill-opacity': .7,
        }

        if (id === 'food-desert-polygons') {

            this.setState(prevState => ({
                legendInfo: [
                    ...prevState.legendInfo,
                    [{ color: medium, value: 'food' }],
                ]
            }));

            return {
                'fill-color': [
                    "case",
                    ["==", ["to-number", ["get", variable]], 1], medium,
                    "transparent"
                ],
                ...otherStyles,
            }
        } else if (id === 'transit-polygons') {

            this.setState(prevState => ({
                legendInfo: [
                    ...prevState.legendInfo,
                    [{ color: medium, value: 'transit' }],
                ]
            }));

            return {
                'fill-color': medium,
                ...otherStyles,
            }
        } else if (id === 'transportation-polygons' || id === 'race-polygons') {
            const q0 = 0;
            const q1 = id === 'race-polygons' ? 20 : 0.2;
            const q2 = id === 'race-polygons' ? 40 : 0.4;
            const q3 = id === 'race-polygons' ? 60 : 0.6;
            const q4 = id === 'race-polygons' ? 80 : 0.8;
            const q5 = id === 'race-polygons' ? 100 : 1;

            this.setState(prevState => ({
                legendInfo: [
                    ...prevState.legendInfo,
                    [
                        { color: lightest, value: { min: id === 'race-polygons' ? q0 / 100 : q0, max: id === 'race-polygons' ? q1 / 100 : q1 } },
                        { color: lightest, value: { min: id === 'race-polygons' ? q1 / 100 : q1, max: id === 'race-polygons' ? q2 / 100 : q2 } },
                        { color: medium, value: { min: id === 'race-polygons' ? q2 / 100 : q2, max: id === 'race-polygons' ? q3 / 100 : q3 } },
                        { color: dark, value: { min: id === 'race-polygons' ? q3 / 100 : q3, max: id === 'race-polygons' ? q4 / 100 : q4 } },
                        { color: darkest, value: { min: id === 'race-polygons' ? q4 / 100 : q4, max: id === 'race-polygons' ? q5 / 100 : q5 } },
                    ]
                ]
            }))

            return {
                'fill-color': [
                    "step",
                    ["to-number", ["get", variable]],
                    lightest,
                    q1, light,
                    q2, medium,
                    q3, dark,
                    q4, darkest
                ],
                ...otherStyles,
            }
        } else if (id === 'loss-affordable-polygons') {
            const loss = array.filter(item => item < 0)
            const gain = array.filter(item => item > 0)

            const q0L = quantile(loss, 0);
            const q1L = quantile(loss, 0.25);
            const q2L = quantile(loss, 0.5);
            const q3L = quantile(loss, 0.75);
            const q4L = quantile(loss, 1);

            const q6G = quantile(gain, 0);
            const q7G = quantile(gain, 0.25);
            const q8G = quantile(gain, 0.5);
            const q9G = quantile(gain, 0.75);
            const q10G = quantile(gain, 1);

            this.setState(prevState => ({
                legendInfo: [
                    ...prevState.legendInfo,
                    [
                        { color: cc.neg5, value: { min: q0L, max: q1L } },
                        { color: cc.neg4, value: { min: q1L, max: q2L } },
                        { color: cc.neg3, value: { min: q2L, max: q3L } },
                        { color: cc.neg2, value: { min: q3L, max: q4L } },
                        { color: cc.neutral, value: { min: q4L, max: q6G } },
                        { color: cc.pos2, value: { min: q6G, max: q7G } },
                        { color: cc.pos3, value: { min: q7G, max: q8G } },
                        { color: cc.pos4, value: { min: q8G, max: q9G } },
                        { color: cc.pos5, value: { min: q9G, max: q10G } },
                    ]
                ]
            }))

            return {
                'fill-color': [
                    "step",
                    ["to-number", ["get", variable]],
                    cc.neg5,
                    q1L, cc.neg4,
                    q2L, cc.neg3,
                    q3L, cc.neg2,
                    q4L, cc.neutral,
                    q6G, cc.pos1,
                    q7G, cc.pos2,
                    q8G, cc.pos3,
                    q9G, cc.pos4,

                ],
                ...otherStyles,
            }

        } else {
            const cleanArr = array.filter(item => item)

            // console.log(cleanArr)
            const q0 = quantile(cleanArr, 0);
            const q1 = quantile(cleanArr, 0.2);
            const q2 = quantile(cleanArr, 0.4);
            const q3 = quantile(cleanArr, 0.6);
            const q4 = quantile(cleanArr, 0.8);
            const q5 = quantile(cleanArr, 1);

            this.setState(prevState => ({
                legendInfo: [
                    ...prevState.legendInfo,
                    [
                        { color: lightest, value: { min: id === 'rising-rent-polygons' ? q0 - 1 : q0, max: id === 'rising-rent-polygons' ? q1 - 1 : q1 } },
                        { color: lightest, value: { min: id === 'rising-rent-polygons' ? q1 - 1 : q1, max: id === 'rising-rent-polygons' ? q2 - 1 : q2 } },
                        { color: medium, value: { min: id === 'rising-rent-polygons' ? q2 - 1 : q2, max: id === 'rising-rent-polygons' ? q3 - 1 : q3 } },
                        { color: dark, value: { min: id === 'rising-rent-polygons' ? q3 - 1 : q3, max: id === 'rising-rent-polygons' ? q4 - 1 : q4 } },
                        { color: darkest, value: { min: id === 'rising-rent-polygons' ? q4 - 1 : q4, max: id === 'rising-rent-polygons' ? q5 - 1 : q5 } },
                    ]
                ]
            }))

            return {
                'fill-color': [
                    "step",
                    ["to-number", ["get", variable]],
                    lightest,
                    q1, light,
                    q2, medium,
                    q3, dark,
                    q4, darkest
                ],
                ...otherStyles,
            }
        }
    }

    loadInterpolatedPolygonGeography = (map, data, id, variable, { lightest, light, medium, dark, darkest }) => {
        const arr = data.features.map(f => f.properties[variable]);
        const arrSorted = arr.sort((a, b) => a - b);

        map.addLayer({
            id: id,
            type: 'fill',
            source: {
                type: 'geojson',
                data: data,
            },
            layout: {},
            paint: this.getPaintProps(arrSorted, id, variable, { lightest, light, medium, dark, darkest })
        })

        map.setLayoutProperty(id, 'visibility', 'none')
    }

    loadInterpolatedPointGeography = (map, data, id) => {
        map.addLayer({
            id: id,
            type: "symbol",
            source: {
                type: 'geojson',
                data: data,
            },
            // filter: ["all", ["==", "Aggregated", 1], ["!has", "point_count"]],
            // filter: ["!has", "point_count"],
 
            layout: {
                // "icon-image": ["case",
                //     ["get", "active"], 'marker',
                //     'active-marker'],
                "icon-image": 'bus',
                "icon-size": 1,
                'icon-anchor': 'bottom',
            }
        });

        map.setLayoutProperty(id, 'visibility', 'none')
    }

    loadLineGeography = (map, data, id) => {
        // map.addLayer({
        //     id: id+'background',
        //     type: 'line',
        //     source: {
        //         type: 'geojson',
        //         data: data,
        //     },
        //     layout: {
        //         'line-join': 'round',
        //         'line-cap': 'round',
        //     },
        //     paint: {
        //         'line-color': '#f6f6f6',
        //         'line-width': 20,
        //         'line-opacity':0.6
        //     },
        // })

        map.addLayer({
            id: id,
            type: 'line',
            source: {
                type: 'geojson',
                data: data,
            },
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-color': '#3AD930',
                'line-width': 5,
                'line-opacity':1
            },
        })

        //map.setLayoutProperty(id, 'visibility', 'none')
    }

    render() {
        const { switches } = this.props;
        const { legendInfo } = this.state;

        const style = {
            height: '100%',
            width: '100%',
        };

        const legendMapping = {
            foodDeserts: 0,
            transportation: 1,
            transit: 2,
            affordableUnits: 3,
            risingRents: 4,
            race: 5,
            income: 6,
        }

        const whichLayer = getKeyByValue(switches, true);
        const index = whichLayer ? legendMapping[whichLayer] : null;
        const legendContent = index !== null ? legendInfo[index] : null;

        return (
            <Fragment>
            <div style={style} ref={this.mapContainer} />
            {legendContent && (
                <Legend
                    layerName={whichLayer}
                    content={legendContent}
                />
            )}
            </Fragment>
        )
    }
}

export default Map
